import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { motion } from 'framer-motion';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;

const QuoteBookingAnalytics = () => {
    const [quotes, setQuotes] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [quotesRes, metricsRes] = await Promise.all([
                axios.get(`${API}/quotes`),
                axios.get(`${API}/quotes/metrics`)
            ]);
            setQuotes(quotesRes.data);
            setMetrics(metricsRes.data);
        } catch (error) {
            console.error('Error fetching quote data:', error);
        } finally {
            setLoading(false);
        }
    };

    const conversionData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
            {
                label: 'Conversion Rate %',
                data: [58, 62, 65, 68, 68, 68],
                borderColor: '#8b5cf6',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                tension: 0.4,
                fill: true,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: '#94a3b8',
                },
            },
        },
        scales: {
            x: {
                ticks: { color: '#94a3b8' },
                grid: { color: 'rgba(148, 163, 184, 0.1)' },
            },
            y: {
                ticks: { color: '#94a3b8' },
                grid: { color: 'rgba(148, 163, 184, 0.1)' },
                min: 50,
                max: 75,
            },
        },
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 animate-pulse">
                    <div className="h-48 bg-slate-200 dark:bg-slate-800 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
            data-testid="quote-booking-analytics"
        >
            {/* AI Insight Banner */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold mb-2">Quote Performance Insights</h3>
                        <p className="text-white/90">
                            Quote-to-booking ratio improved by 9% this month. Bookings from China lanes show higher cancellation risk.
                            <span className="font-bold">Recommend: Enable auto-quoting for repeat customers to boost conversion.</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <FileText className="w-8 h-8 text-blue-500 mb-3" />
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Total Quotes</div>
                    <div className="text-3xl font-bold text-slate-900 dark:text-white">{metrics?.total_quotes || 0}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Converted</div>
                    <div className="text-3xl font-bold text-green-500">{metrics?.converted || 0}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <TrendingUp className="w-8 h-8 text-purple-500 mb-3" />
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Conversion Rate</div>
                    <div className="text-3xl font-bold text-purple-500">{metrics?.conversion_rate || 0}%</div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <Clock className="w-8 h-8 text-orange-500 mb-3" />
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Avg Response Time</div>
                    <div className="text-3xl font-bold text-orange-500">{metrics?.avg_response_time_hours || 0}h</div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Avg Quote Rate</div>
                    <div className="text-3xl font-bold text-blue-500">${metrics?.avg_rate?.toFixed(0) || 0}</div>
                </div>
            </div>

            {/* Quote Conversion Trend */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Quote-to-Booking Conversion Trend (%)</h3>
                <div style={{ height: '300px' }}>
                    <Line data={conversionData} options={chartOptions} />
                </div>
            </div>

            {/* Recent Quotes Table */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Recent RFQs & Quotes</h3>
                <div className="overflow-x-auto">
                    <table className="w-full" data-testid="quotes-table">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">RFQ Number</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Customer</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Route</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Carrier</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Rate</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Response Time</th>
                                <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotes?.slice(0, 15)?.map((quote, index) => (
                                <tr
                                    key={quote.id}
                                    data-testid={`quote-row-${index}`}
                                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <td className="py-4 px-4 text-sm font-medium text-slate-900 dark:text-white">{quote.rfq_number}</td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">{quote.customer}</td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">
                                        {quote.origin} → {quote.destination}
                                    </td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">{quote.carrier}</td>
                                    <td className="py-4 px-4 text-right text-sm font-semibold text-slate-900 dark:text-white">
                                        ${quote.rate.toFixed(0)}
                                    </td>
                                    <td className="py-4 px-4 text-right text-sm text-slate-600 dark:text-slate-400">
                                        {quote.response_time_hours}h
                                    </td>
                                    <td className="py-4 px-4 text-center">
                                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${quote.status === 'Converted' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                            quote.status === 'Quoted' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                                                quote.status === 'Pending' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                                                    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                            }`}>
                                            {quote.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </motion.div>
    );
};

export default QuoteBookingAnalytics;